import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import { Product } from './useProducts';

export interface ProductBundle {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  bundle_price: number;
  currency: string;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  items?: ProductBundleItem[];
}

export interface ProductBundleItem {
  id: string;
  bundle_id: string;
  product_id: string;
  quantity: number;
  created_at: string;
  product?: Product;
}

export function useProductBundles() {
  return useQuery({
    queryKey: ['product-bundles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_bundles')
        .select(`
          *,
          items:product_bundle_items(
            *,
            product:products(*)
          )
        `)
        .order('name');

      if (error) throw error;
      return data as ProductBundle[];
    },
  });
}

export function useCreateProductBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bundle: {
      name: string;
      description?: string;
      bundle_price: number;
      currency: string;
      discount_type: string;
      discount_value: number;
      is_active: boolean;
      valid_from?: string | null;
      valid_until?: string | null;
      items: { product_id: string; quantity: number }[];
    }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      // Create the bundle
      const { data: newBundle, error: bundleError } = await supabase
        .from('product_bundles')
        .insert({
          tenant_id: profile.tenant_id,
          name: bundle.name,
          description: bundle.description,
          bundle_price: bundle.bundle_price,
          currency: bundle.currency,
          discount_type: bundle.discount_type,
          discount_value: bundle.discount_value,
          is_active: bundle.is_active,
          valid_from: bundle.valid_from,
          valid_until: bundle.valid_until,
        })
        .select()
        .single();

      if (bundleError) throw bundleError;

      // Add bundle items
      if (bundle.items.length > 0) {
        const bundleItems = bundle.items.map((item) => ({
          bundle_id: newBundle.id,
          product_id: item.product_id,
          quantity: item.quantity,
        }));

        const { error: itemsError } = await supabase
          .from('product_bundle_items')
          .insert(bundleItems);

        if (itemsError) throw itemsError;
      }

      return newBundle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
      toast.success('Bundle created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create bundle: ' + error.message);
    },
  });
}

export function useUpdateProductBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      items,
      ...updates
    }: {
      id: string;
      name?: string;
      description?: string | null;
      bundle_price?: number;
      currency?: string;
      discount_type?: string;
      discount_value?: number;
      is_active?: boolean;
      valid_from?: string | null;
      valid_until?: string | null;
      items?: { product_id: string; quantity: number }[];
    }) => {
      // Update bundle
      const { error: updateError } = await supabase
        .from('product_bundles')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      // Update items if provided
      if (items) {
        // Delete existing items
        await supabase.from('product_bundle_items').delete().eq('bundle_id', id);

        // Insert new items
        if (items.length > 0) {
          const bundleItems = items.map((item) => ({
            bundle_id: id,
            product_id: item.product_id,
            quantity: item.quantity,
          }));

          const { error: itemsError } = await supabase
            .from('product_bundle_items')
            .insert(bundleItems);

          if (itemsError) throw itemsError;
        }
      }

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
      toast.success('Bundle updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update bundle: ' + error.message);
    },
  });
}

export function useDeleteProductBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_bundles').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-bundles'] });
      toast.success('Bundle deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete bundle: ' + error.message);
    },
  });
}

// Calculate bundle savings
export function calculateBundleSavings(bundle: ProductBundle): {
  originalPrice: number;
  bundlePrice: number;
  savings: number;
  savingsPercent: number;
} {
  const originalPrice =
    bundle.items?.reduce((sum, item) => {
      const productPrice = item.product?.base_price || 0;
      return sum + productPrice * item.quantity;
    }, 0) || 0;

  const bundlePrice = bundle.bundle_price;
  const savings = originalPrice - bundlePrice;
  const savingsPercent = originalPrice > 0 ? (savings / originalPrice) * 100 : 0;

  return { originalPrice, bundlePrice, savings, savingsPercent };
}
